import { IconButton, Skeleton, Stack, Typography } from '@mui/material';

import ChevronRightIcon from '@/components/Icons/ChevronRightIcon';
import KeyIcon from '@/components/Icons/KeyIcon';
import ShieldIcon from '@/components/Icons/ShieldIcon';
import UserIcon from '@/components/Icons/UserIcon';
import useIsMultipleTeamsEnabled from '@/hooks/useIsMultipleTeamsEnabled';
import { Tabs } from '@/pages/Settings/Tabs/types';
import { BasetenPageEnum } from '@/routes/types';
import { useGrandTotalCost } from '@/sections/Billing/hooks';
import { pluralizeString } from '@/utils/pluralizeString';

import { AppLink, ListItem } from '../styled';
import { useApiKeys, useSecrets, useUsers } from './hooks';
import { Section, SettingsItemIcon } from './styled';

function Settings() {
  const isMultipleTeamsEnabled = useIsMultipleTeamsEnabled();
  const { secrets, loading: isSecretsLoading } = useSecrets(isMultipleTeamsEnabled);
  const { apiKeys, loading: isApiKeysLoading } = useApiKeys();
  const { users, loading: isUsersLoading } = useUsers();
  const { formattedTotal, loading: isGrandTotalCostLoading } = useGrandTotalCost();

  return (
    <Section gap={1}>
      <Typography variant="h4" ml={2} mt={2}>
        Total current spend
      </Typography>
      <AppLink page={BasetenPageEnum.Settings} params={{ tabId: Tabs.Billing }}>
        <ListItem>
          <Stack gap={1} pl={1} py={1}>
            <Typography variant="h3">
              {isGrandTotalCostLoading ? <Skeleton variant="text" width={100} /> : formattedTotal}
            </Typography>
            <Typography variant="h5" color="text.subtle">
              This billing period
            </Typography>
          </Stack>
          <IconButton data-cy="list-item-arrow">
            <ChevronRightIcon />
          </IconButton>
        </ListItem>
      </AppLink>
      <AppLink page={BasetenPageEnum.Settings} params={{ tabId: Tabs.Members }}>
        <ListItem>
          <Stack direction="row" gap={1.5} alignItems="center">
            <SettingsItemIcon>
              <UserIcon color="secondary" />
            </SettingsItemIcon>
            {isUsersLoading ? (
              <Skeleton variant="text" width={100} />
            ) : (
              <Stack gap={0.5} direction="row" alignItems="center">
                <Typography variant="h5">{users.length}</Typography>
                <Typography variant="h5" color="text.muted">
                  {pluralizeString(users.length, 'member', undefined, undefined, false)}
                </Typography>
              </Stack>
            )}
          </Stack>
          <IconButton data-cy="list-item-arrow">
            <ChevronRightIcon />
          </IconButton>
        </ListItem>
      </AppLink>
      <AppLink page={BasetenPageEnum.Settings} params={{ tabId: Tabs.ApiKeys }}>
        <ListItem>
          <Stack direction="row" gap={1.5} alignItems="center">
            <SettingsItemIcon>
              <KeyIcon color="secondary" />
            </SettingsItemIcon>
            {isApiKeysLoading ? (
              <Skeleton variant="text" width={100} />
            ) : (
              <Stack gap={0.5} direction="row" alignItems="center">
                <Typography variant="h5">{apiKeys.length}</Typography>
                <Typography variant="h5" color="text.muted">
                  {pluralizeString(apiKeys.length, 'API key', undefined, undefined, false)}
                </Typography>
              </Stack>
            )}
          </Stack>
          <IconButton data-cy="list-item-arrow">
            <ChevronRightIcon />
          </IconButton>
        </ListItem>
      </AppLink>
      {!isMultipleTeamsEnabled && (
        <AppLink page={BasetenPageEnum.Settings} params={{ tabId: Tabs.Secrets }}>
          <ListItem>
            <Stack direction="row" gap={1.5} alignItems="center">
              <SettingsItemIcon>
                <ShieldIcon color="secondary" />
              </SettingsItemIcon>
              {isSecretsLoading ? (
                <Skeleton variant="text" width={100} />
              ) : (
                <Stack gap={0.5} direction="row" alignItems="center">
                  <Typography variant="h5">{secrets.length}</Typography>
                  <Typography variant="h5" color="text.muted">
                    {pluralizeString(secrets.length, 'secret', undefined, undefined, false)}
                  </Typography>
                </Stack>
              )}
            </Stack>
            <IconButton data-cy="list-item-arrow">
              <ChevronRightIcon />
            </IconButton>
          </ListItem>
        </AppLink>
      )}
    </Section>
  );
}

export default Settings;
